// src/components/CreateConfigModal.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useNotification, notifyPermissionError } from '@1823-partners/core';
import {
  createConfig,
  fetchScenarios,
  fetchScenarioGroups,
  fetchBooks,
  fetchCusips,
  fetchLatestAsofdt,
} from "../api";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { notifyUngatedFailure } from '../utils/notifyDenial';
import { SITE_ENV, envChoices } from '../utils/siteEnv';
import { SAVE_OUTCOME_UNKNOWN, isConflict, serverErrorText } from '../utils/serverErrors';
import ScenarioPickerTools from './ScenarioPickerTools';

// ASOFDT is a calendar date stored as UTC midnight; read it in UTC so the
// browser's zone never shifts it to the previous day.
dayjs.extend(utc);

// A group with both US and BM scenarios on the date becomes one option per prefix.
const groupOptions = (groups) => {
  const prefixCount = {};
  groups.forEach(({ group }) => {
    prefixCount[group] = (prefixCount[group] || 0) + 1;
  });
  return groups.map(({ group, prefix, scenids }) => ({
    label: prefixCount[group] > 1 ? `${group} (${prefix})` : group,
    scenids,
  }));
};

// initialData edits that config. copyFrom (without initialData) creates a new config
// prefilled from that one: it saves as a create, so the source is never written.
const CreateConfigModal = ({ open, onClose, onCreated, onConflict, userId, initialData, copyFrom }) => {
  const { showNotification } = useNotification();
  const isCopy = !initialData && Boolean(copyFrom);
  const defaultForm = {
    ID: "",
    SCEN_IDS: [],
    CUSIPS: [],
    ASOFDT: "",
    ENV: SITE_ENV,
    BOOK: "",
    DESCRIPTION: "",
    SKIP_INTEX: false,
    SKIP_ADCO: false,
    IA_ONLY: false,
    HELLO_WORLD: false,
    ADCO_CSHF_BOND: false,
    ADCO_CSHF_LOAN: false,
    CUSTOM_PM_BRANCH: "",
    CUSTOM_PKWA_BRANCH: "",
    SAVE: false,
    MODIFIED_BY: userId,
  };

  const [formData, setFormData] = useState(initialData || defaultForm);
  const [loading, setLoading] = useState(false);
  const [availableScenarios, setAvailableScenarios] = useState([]);
  const [scenarioGroups, setScenarioGroups] = useState([]);
  const [availableCusips, setAvailableCusips] = useState([]);
  const [availableBooks, setAvailableBooks] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [cusipsLoading, setCusipsLoading] = useState(false);
  const [scenariosLoading, setScenariosLoading] = useState(false);

  const [latestDateLoading, setLatestDateLoading] = useState(false);
  const [dateMessage, setDateMessage] = useState('');
  const dateEdited = useRef(false);

  useEffect(() => {
    let active = true;
    setAvailableScenarios([]);
    setScenarioGroups([]);
    setAvailableBooks([]);
    setAvailableCusips([]);
    // Don't fetch if modal is closed or required fields are missing
    if (!open || !formData.ENV || !formData.ASOFDT) {
      return;
    }

    // Set loading states
    setScenariosLoading(true);
    setCusipsLoading(true);

    // Debounce API calls by 500ms to prevent excessive requests
    const timeoutId = setTimeout(async () => {
      try {
        // Fetch all data in parallel
        const [scenariosResponse, groupsResponse, booksResponse, cusipsResponse] = await Promise.all([
          fetchScenarios(formData.ENV, formData.ASOFDT).catch(err => {
            console.error("Error fetching scenarios", err);
            notifyUngatedFailure(showNotification, err);
            return { data: [] };
          }),
          // Fails in DEV (its mapping table has no SCENARIO_GROUPING column yet) and with a 403
          // for callers without scenario write, who are refused at save with a request-access
          // prompt instead. No notification either way: the form just offers no groups.
          fetchScenarioGroups(formData.ENV, formData.ASOFDT).catch(err => {
            console.error("Error fetching scenario groups", err);
            return { data: [] };
          }),
          fetchBooks(formData.ENV, formData.ASOFDT).catch(err => {
            console.error("Error fetching books", err);
            notifyUngatedFailure(showNotification, err);
            return { data: [] };
          }),
          fetchCusips(formData.ENV, formData.ASOFDT).catch(err => {
            console.error("Error fetching cusips", err);
            notifyUngatedFailure(showNotification, err);
            return { data: [] };
          })
        ]);

        if (!active) return;
        setAvailableScenarios(scenariosResponse.data || []);
        setScenarioGroups(groupsResponse.data || []);
        setAvailableBooks(booksResponse.data || []);
        setAvailableCusips(cusipsResponse.data || []);
      } catch (error) {
        console.error("Error loading data", error);
      } finally {
        if (active) {
          setScenariosLoading(false);
          setCusipsLoading(false);
        }
      }
    }, 500);

    return () => {
      active = false;
      clearTimeout(timeoutId);
      setScenariosLoading(false);
      setCusipsLoading(false);
    };
  }, [open, formData.ENV, formData.ASOFDT, showNotification]);

  // When initialData changes (i.e. when editing an existing config), update the form.
  // Also reset form when modal opens without initial data (creating new config)
  useEffect(() => {
    if (!open) {
      return; // Don't update form when modal is closed
    }

    let active = true;
    dateEdited.current = false;
    setFormErrors({});
    setDateMessage('');
    setLatestDateLoading(false);

    // The config being edited, or the one being copied.
    const source = initialData || copyFrom;
    if (source) {
      // If SCEN_IDS is a string, split it into an array; otherwise assume it's already an array.
      // Handle null/undefined values by defaulting to empty array
      const parsedScenIds = source.SCEN_IDS
        ? (typeof source.SCEN_IDS === "string"
            ? source.SCEN_IDS.split(",").filter(id => id.trim())
            : Array.isArray(source.SCEN_IDS)
              ? source.SCEN_IDS
              : [])
        : [];

      const parsedCusips = source.CUSIPS
        ? (typeof source.CUSIPS === "string"
            ? source.CUSIPS.split(",").filter(cusip => cusip.trim())
            : Array.isArray(source.CUSIPS)
              ? source.CUSIPS
              : [])
        : [];

      const formattedDate = source.ASOFDT ? formatDate(source.ASOFDT) : "";

      setFormData({
        ...source,
        SCEN_IDS: parsedScenIds,
        CUSIPS: parsedCusips,
        ASOFDT: formattedDate,
        // A copy is a new config: no ID until the server assigns one (or the user types
        // one), and this user as its author. Every other field, flags included, carries over.
        ...(initialData ? {} : { ID: "", MODIFIED_BY: userId }),
      });
    } else {
      // Reset to empty form when creating new config
      setFormData({
        ...defaultForm,
        SCEN_IDS: [],  // Explicitly ensure empty
        CUSIPS: [],    // Explicitly ensure empty
        MODIFIED_BY: userId
      });
      setLatestDateLoading(true);
      fetchLatestAsofdt(SITE_ENV)
        .then(({ data }) => {
          if (!active || dateEdited.current) return;
          if (data?.asofdt) {
            setFormData(previous => ({ ...previous, ASOFDT: data.asofdt }));
          } else {
            setDateMessage(`No active forward-rate date is available in ${SITE_ENV}. Enter an as-of date.`);
          }
        })
        .catch(() => {
          if (active && !dateEdited.current) {
            setDateMessage(`Unable to load the latest ${SITE_ENV} forward-rate date. Enter an as-of date.`);
          }
        })
        .finally(() => {
          if (active) setLatestDateLoading(false);
        });
    }
    return () => { active = false; };
    // Reset only when the modal opens or the config being edited or copied changes. A userId
    // change re-running it would wipe a draft in progress; defaultForm is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, copyFrom, open]);


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'ASOFDT') {
      dateEdited.current = true;
      setDateMessage('');
      setLatestDateLoading(false);
    }
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    // Clear field-specific errors when user makes changes
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    const date = dayjs.utc(dateStr);
    return date.format("YYYY-MM-DD");
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.SCEN_IDS || !Array.isArray(formData.SCEN_IDS) || formData.SCEN_IDS.length === 0) {
      errors.SCEN_IDS = 'At least one scenario is required';
    }

    if (!formData.ASOFDT) {
      errors.ASOFDT = 'As of date is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    setLoading(true);

    // Built inside the try: a throw here would otherwise skip both the catch and
    // setLoading(false), leaving the modal spinning with no error shown.
    try {
      const asofdtFormatted = formatDate(formData.ASOFDT);

      // Join selected scenario IDs and CUSIPs into comma-separated strings
      const scenIds = formData.SCEN_IDS?.length > 0 ? formData.SCEN_IDS.join(",") : null;
      const cusips = formData.CUSIPS?.length > 0 ? formData.CUSIPS.join(",") : null;

      // Prepare data with null for empty/blank fields. String() because these are
      // spread from a fetched config, whose values are not guaranteed to be strings —
      // .trim() on a number throws, and ?. only guards null/undefined.
      const dataToSubmit = {
        ...formData,
        SCEN_IDS: scenIds,
        CUSIPS: cusips,
        ASOFDT: asofdtFormatted || null,
        BOOK: String(formData.BOOK ?? "").trim() || null,
        DESCRIPTION: String(formData.DESCRIPTION ?? "").trim() || null,
        ID: String(formData.ID ?? "").trim(),
        // HELLO_WORLD needs the IA run this job performs: only with Intex ADCO Only
        // and never when Intex is skipped. Stored values that break that are dropped.
        HELLO_WORLD: Boolean(formData.HELLO_WORLD) && Boolean(formData.IA_ONLY) && !formData.SKIP_INTEX,
        // Stored configs predating these columns load them as null; save them as off.
        ADCO_CSHF_BOND: Boolean(formData.ADCO_CSHF_BOND),
        ADCO_CSHF_LOAN: Boolean(formData.ADCO_CSHF_LOAN),
        // The save's concurrency token: the MODIFIED_DATE this config was loaded with,
        // unchanged, or null for a new config. The server refuses a stale one with 409.
        MODIFIED_DATE: initialData ? initialData.MODIFIED_DATE ?? null : null,
        mode: initialData ? "edit" : "create",
      };

      await createConfig(dataToSubmit);
      onCreated && onCreated();
      onClose();
      setFormData(defaultForm);
    } catch (error) {
      console.error("Error creating/updating config", error);
      if (!notifyPermissionError(showNotification, error)) {
        const errorData = error.response?.data;
        const message = serverErrorText(error, {
          fallback: "Error creating/updating config",
          unknown: SAVE_OUTCOME_UNKNOWN,
        });
        if (errorData?.field && typeof errorData.error === "string") {
          setFormErrors((previous) => ({
            ...previous,
            [errorData.field]: errorData.error,
          }));
        }
        // Someone saved this config after it was loaded: refresh the grid so it can be
        // reopened with their changes. These edits stay in the form to re-apply.
        if (isConflict(error) && !errorData?.field) {
          onConflict?.();
        }
        showNotification(message, "error");
      }
    }
    setLoading(false);
  };

  // The bulk scenario controls (select all, clear, paste) replace the whole selection.
  const setScenIds = (ids) => {
    setFormData(previous => ({ ...previous, SCEN_IDS: ids }));
    if (formErrors.SCEN_IDS) {
      setFormErrors(prev => ({ ...prev, SCEN_IDS: null }));
    }
  };

  const groupChoices = groupOptions(scenarioGroups);
  const selectedScenIds = new Set(formData.SCEN_IDS || []);
  const selectedGroups = groupChoices.filter(option =>
    option.scenids.every(id => selectedScenIds.has(id))
  );

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {initialData
          ? "Edit Scenario Config"
          : isCopy
          ? "Copy to New Scenario Config"
          : "Create New Scenario Config"}
      </DialogTitle>
      <form onSubmit={handleSubmit} noValidate>
        <DialogContent dividers>
          {isCopy && (
            <Alert severity="info">
              {`Copied from ${copyFrom.ID}. Saving creates a new config with its own ID; ${copyFrom.ID} is not changed.`}
            </Alert>
          )}
          <TextField
            fullWidth
            margin="normal"
            label="Config ID"
            name="ID"
            value={formData.ID || ""}
            onChange={handleChange}
            InputProps={{ readOnly: Boolean(initialData) }}
            error={!!formErrors.ID}
            helperText={
              formErrors.ID ||
              (initialData
                ? "Config IDs cannot be renamed"
                : "Optional - leave blank to generate a timestamp ID")
            }
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="env-label">Environment</InputLabel>
            <Select
              labelId="env-label"
              label="Environment"
              name="ENV"
              value={formData.ENV}
              onChange={handleChange}
            >
              {envChoices(formData.ENV).map(({ value, disabled }) => (
                <MenuItem key={value} value={value} disabled={disabled}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label="Asof Date"
            name="ASOFDT"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formData.ASOFDT || ""}
            onChange={handleChange}
            required
            error={!!formErrors.ASOFDT}
            helperText={formErrors.ASOFDT || (latestDateLoading ? `Loading latest ${SITE_ENV} forward-rate date...` : dateMessage)}
          />
          {/* Groups are never saved: a group shows as selected while all its IDs are in
              SCEN_IDS, and adding or removing one adds or removes those IDs */}
          <Autocomplete
            multiple
            options={groupChoices}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.label === value.label}
            value={selectedGroups}
            loading={scenariosLoading}
            loadingText="Loading scenario groups..."
            noOptionsText={scenariosLoading ? "Loading..." : "No scenario groups for this date"}
            onChange={(event, newValue) => {
              const kept = new Set(newValue.map(option => option.label));
              const removedIds = new Set(
                selectedGroups.filter(option => !kept.has(option.label)).flatMap(option => option.scenids)
              );
              const remaining = (formData.SCEN_IDS || []).filter(id => !removedIds.has(id));
              const added = newValue
                .flatMap(option => option.scenids)
                .filter(id => !remaining.includes(id));
              setFormData({ ...formData, SCEN_IDS: [...remaining, ...added] });
              if (formErrors.SCEN_IDS) {
                setFormErrors(prev => ({
                  ...prev,
                  SCEN_IDS: null
                }));
              }
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.label}
                <span style={{ marginLeft: 'auto', color: '#6c757d' }}>{option.scenids.length}</span>
              </li>
            )}
            disableCloseOnSelect={true}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Scenario groups"
                margin="normal"
                helperText="Selecting a group adds its scenarios for this date below; removing it removes them"
              />
            )}
          />
          {/* Autocomplete for searchable multi-select of Scenario IDs */}
          <Autocomplete
            multiple
            options={['SELECT_ALL', ...(availableScenarios || []).sort()]}
            getOptionLabel={(option) => option === 'SELECT_ALL' ? 'Select All' : (option || '')}
            value={formData.SCEN_IDS || []}
            loading={scenariosLoading}
            loadingText="Loading scenarios..."
            noOptionsText={scenariosLoading ? "Loading..." : "No scenarios available"}
            onChange={(event, newValue, reason) => {
              // Check if "Select All" was clicked
              if (newValue.includes('SELECT_ALL')) {
                // If Select All is now in the array, select everything except SELECT_ALL itself
                const allScenarios = (availableScenarios || []).sort();
                setFormData({ ...formData, SCEN_IDS: allScenarios });
              } else {
                setFormData({ ...formData, SCEN_IDS: newValue || [] });
              }

              // Clear scenarios error when user selects scenarios
              if (formErrors.SCEN_IDS) {
                setFormErrors(prev => ({
                  ...prev,
                  SCEN_IDS: null
                }));
              }
            }}
            renderOption={(props, option, { selected }) => {
              // Special handling for Select All option
              if (option === 'SELECT_ALL') {
                // Only show as checked if there are items AND all are selected
                const allSelected = availableScenarios.length > 0 &&
                                   formData.SCEN_IDS?.length === availableScenarios.length;
                return (
                  <li {...props} style={{ fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={allSelected}
                    />
                    Select All
                  </li>
                );
              }

              return (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option}
                </li>
              );
            }}
            disableCloseOnSelect={true}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Scenarios"
                margin="normal"
                required
                error={!!formErrors.SCEN_IDS}
                helperText={formErrors.SCEN_IDS || "Select one or more scenarios"}
              />
            )}
          />
          <ScenarioPickerTools
            available={availableScenarios}
            selected={formData.SCEN_IDS || []}
            asofdt={formData.ASOFDT}
            loading={scenariosLoading}
            onChange={setScenIds}
          />
          <Autocomplete
            multiple
            freeSolo
            options={['SELECT_ALL', ...(availableCusips || []).sort()]}
            getOptionLabel={(option) => option === 'SELECT_ALL' ? 'Select All' : (option || '')}
            value={formData.CUSIPS || []}
            loading={cusipsLoading}
            loadingText="Loading CUSIPs..."
            noOptionsText={cusipsLoading ? "Loading..." : "No CUSIPs available"}
            onChange={(event, newValue) => {
              // Check if "Select All" was clicked
              if (newValue.includes('SELECT_ALL')) {
                // If Select All is now in the array, select everything except SELECT_ALL itself
                const allCusips = (availableCusips || []).sort();
                setFormData({ ...formData, CUSIPS: allCusips });
              } else {
                setFormData({ ...formData, CUSIPS: newValue || [] });
              }
            }}
            filterOptions={(options, { inputValue }) => {
              // Fast filtering for large datasets
              if (!inputValue) {
                // When no search, limit to first 100 items for performance
                return options.slice(0, 101);
              }

              const searchLower = inputValue.toLowerCase();
              const filtered = [];

              // Always include SELECT_ALL at top if it matches search
              if ('select all'.includes(searchLower)) {
                filtered.push('SELECT_ALL');
              }

              // Filter through CUSIPs efficiently, limit results to 200
              for (let i = 1; i < options.length && filtered.length < 201; i++) {
                if (options[i].toLowerCase().includes(searchLower)) {
                  filtered.push(options[i]);
                }
              }

              return filtered;
            }}
            ListboxProps={{
              style: { maxHeight: '400px' }
            }}
            renderOption={(props, option, { selected }) => {
              // Special handling for Select All option
              if (option === 'SELECT_ALL') {
                // Only show as checked if there are items AND all are selected
                const allSelected = availableCusips.length > 0 &&
                                   formData.CUSIPS?.length === availableCusips.length;
                return (
                  <li {...props} style={{ fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={allSelected}
                    />
                    Select All ({availableCusips.length} items)
                  </li>
                );
              }

              return (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option}
                </li>
              );
            }}
            disableCloseOnSelect={true}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`CUSIPs (Optional)${availableCusips.length > 0 ? ` - ${availableCusips.length} total` : ''}`}
                margin="normal"
                helperText={availableCusips.length > 100 ? "Type to search through all CUSIPs. Select All will select all " + availableCusips.length + " CUSIPs." : ""}
              />
            )}
          />
          <Autocomplete
            freeSolo
            forcePopupIcon
            inputValue={formData.BOOK || ''}
            onInputChange={(event, newValue, reason) => {
              if (reason === 'input' || reason === 'clear') {
                setFormData(previous => ({ ...previous, BOOK: newValue }));
              }
            }}
            options={(availableBooks || []).sort()}
            getOptionLabel={(option) => option || ''}
            value={formData.BOOK || null}
            onChange={(event, newValue) =>
              setFormData(previous => ({ ...previous, BOOK: newValue || '' }))
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option}
              </li>
            )}
            disableCloseOnSelect={true}
            renderInput={(params) => (
              <TextField {...params} label="Book (Optional)" margin="normal"
                helperText="Select a book with positive holdings for this environment/date, or type a book name." />
            )}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Description"
            name="DESCRIPTION"
            multiline
            rows={4}
            value={formData.DESCRIPTION}
            onChange={handleChange}
          />
          <FormControlLabel
            control={
              <Checkbox
                name="SKIP_INTEX"
                checked={formData.SKIP_INTEX || false}
                disabled={formData.IA_ONLY || false}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    SKIP_INTEX: e.target.checked,
                    IA_ONLY: false,
                    HELLO_WORLD: false
                  });
                }}
              />
            }
            label="Skip Intex"
          />
          <FormControlLabel
            control={
              <Checkbox
                name="SKIP_ADCO"
                checked={formData.SKIP_ADCO || false}
                disabled={formData.IA_ONLY || false}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    SKIP_ADCO: e.target.checked,
                    IA_ONLY: false
                  });
                }}
              />
            }
            label="Skip ADCO"
          />
          <FormControlLabel
            control={
              <Checkbox
                name="IA_ONLY"
                checked={formData.IA_ONLY || false}
                disabled={(formData.SKIP_INTEX || false) || (formData.SKIP_ADCO || false)}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    IA_ONLY: e.target.checked,
                    SKIP_INTEX: false,
                    SKIP_ADCO: false,
                    HELLO_WORLD: e.target.checked ? (formData.HELLO_WORLD || false) : false
                  });
                }}
              />
            }
            label="Intex ADCO Only"
          />
          <FormControlLabel
            control={
              <Checkbox
                name="HELLO_WORLD"
                checked={formData.HELLO_WORLD || false}
                disabled={!(formData.IA_ONLY || false) || (formData.SKIP_INTEX || false)}
                onChange={handleChange}
              />
            }
            label="Hello World"
          />
          <FormControlLabel
            control={
              <Checkbox
                name="ADCO_CSHF_BOND"
                checked={formData.ADCO_CSHF_BOND || false}
                onChange={handleChange}
              />
            }
            label="ADCO Cashflows (Bonds)"
          />
          <FormControlLabel
            control={
              <Checkbox
                name="ADCO_CSHF_LOAN"
                checked={formData.ADCO_CSHF_LOAN || false}
                onChange={handleChange}
              />
            }
            label="ADCO Cashflows (Loans)"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Custom PAM-MASTER Branch"
            name="CUSTOM_PM_BRANCH"
            value={formData.CUSTOM_PM_BRANCH || ''}
            onChange={handleChange}
            placeholder="pam-master branch name"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Custom PAM-KWA Branch"
            name="CUSTOM_PKWA_BRANCH"
            value={formData.CUSTOM_PKWA_BRANCH || ''}
            onChange={handleChange}
            placeholder="pam-kwa branch name"
          />
          <FormControlLabel
            control={
              <Checkbox
                name="SAVE"
                checked={formData.SAVE}
                onChange={handleChange}
              />
            }
            label="Save Results"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Modified By"
            name="MODIFIED_BY"
            value={formData.MODIFIED_BY}
            disabled
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading} sx={{ color: "black" }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !formData.SCEN_IDS || !Array.isArray(formData.SCEN_IDS) || formData.SCEN_IDS.length === 0 || !formData.ASOFDT}
            sx={{ backgroundColor: "lightgrey", color: "black" }}
          >
            {loading
              ? initialData
                ? "Updating..."
                : "Creating..."
              : initialData
              ? "Update Config"
              : "Create Config"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateConfigModal;
